import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const MENU_GAP = 8;
const VIEWPORT_PADDING = 8;
const FALLBACK_MENU_WIDTH = 172;

function MessageActionsMenu({
  alignment = "end",
  onReply,
  onReplyInThread,
  onPin,
  onInfo,
  onDelete,
}) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ left: 0, top: 0 });

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const triggerRect = trigger.getBoundingClientRect();
    const menuWidth = menuRef.current?.offsetWidth || FALLBACK_MENU_WIDTH;
    const menuHeight = menuRef.current?.offsetHeight || 220;

    let left =
      alignment === "start"
        ? triggerRect.left
        : triggerRect.right - menuWidth;

    if (left + menuWidth > window.innerWidth - VIEWPORT_PADDING) {
      left = window.innerWidth - menuWidth - VIEWPORT_PADDING;
    }

    if (left < VIEWPORT_PADDING) {
      left = VIEWPORT_PADDING;
    }

    let top = triggerRect.bottom + MENU_GAP;
    if (top + menuHeight > window.innerHeight - VIEWPORT_PADDING) {
      top = triggerRect.top - menuHeight - MENU_GAP;
    }

    if (top < VIEWPORT_PADDING) {
      top = VIEWPORT_PADDING;
    }

    setPosition({ left, top });
  }, [alignment]);

  const closeMenu = () => {
    setIsOpen(false);
  };

  const handleAction = (action) => {
    closeMenu();
    if (typeof action === "function") {
      action();
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    updatePosition();

    const handlePointerDown = (event) => {
      if (
        triggerRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return;
      }

      closeMenu();
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, updatePosition]);

  return (
    <div className="message-actions-trigger-wrap">
      <button
        ref={triggerRef}
        type="button"
        className="reaction-option-emoji-btn message-actions-trigger"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label="Message actions"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <i className="fa fa-ellipsis-h message__item-ellipsis" />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="message-actions-menu"
            role="menu"
            style={{ left: `${position.left}px`, top: `${position.top}px` }}
          >
            <button type="button" role="menuitem" onClick={() => handleAction(onReply)}>
              Reply
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => handleAction(onReplyInThread)}
            >
              Reply in thread
            </button>
            <button type="button" role="menuitem" onClick={() => handleAction(onPin)}>
              Pin
            </button>
            <button type="button" role="menuitem" onClick={() => handleAction(onInfo)}>
              Info
            </button>
            {onDelete && (
              <button
                type="button"
                role="menuitem"
                className="message-actions-menu__danger"
                onClick={() => handleAction(onDelete)}
              >
                Delete
              </button>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}

export default MessageActionsMenu;
